import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopMenu from '../TopMenu';
import { getProfile, updateTheme } from '../../services/profileApi';

const THEMES = {
  classic: { name: 'Classic Blue', primary: '#2563eb', accent: '#60a5fa', bg: '#0f172a', surface: '#1e293b', text: '#f8fafc', muted: '#94a3b8' },
  emerald: { name: 'Emerald Green', primary: '#10b981', accent: '#6ee7b7', bg: '#022c22', surface: '#064e3b', text: '#ecfdf5', muted: '#6ee7b7' },
  rose: { name: 'Rose Gold', primary: '#f43f5e', accent: '#fda4af', bg: '#1c0a0f', surface: '#3b0f1c', text: '#fff1f2', muted: '#fda4af' },
  violet: { name: 'Violet', primary: '#8b5cf6', accent: '#c4b5fd', bg: '#0d0520', surface: '#1e0a3c', text: '#f5f3ff', muted: '#c4b5fd' },
  professional: { name: 'Professional', primary: '#e2e8f0', accent: '#94a3b8', bg: '#020617', surface: '#0f172a', text: '#f8fafc', muted: '#64748b' },
  amber: { name: 'Amber Warm', primary: '#f59e0b', accent: '#fcd34d', bg: '#1a0f00', surface: '#2d1a00', text: '#fffbeb', muted: '#fcd34d' },
};

const TABS = [
  { id: 'theme', label: 'Profile Theme', icon: 'TM', desc: 'Change the public profile look and feel.' },
  { id: 'account', label: 'Account', icon: 'AC', desc: 'Manage your personal account settings.' },
  { id: 'privacy', label: 'Privacy', icon: 'PR', desc: 'Control what is visible on your profile.' },
  { id: 'notifications', label: 'Notifications', icon: 'NT', desc: 'Tune alerts and email updates.' },
];

const buildShellBg = (isDark) =>
  isDark
    ? 'radial-gradient(circle at top left, rgba(0, 201, 167, 0.12), transparent 28%), radial-gradient(circle at bottom right, rgba(124, 58, 237, 0.12), transparent 30%), #050508'
    : 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.10), transparent 24%), radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.08), transparent 28%), #f7f8fc';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&display=swap');

  * { box-sizing: border-box; }
  html { scroll-behavior: smooth; }

  .st-shell {
    min-height: 100vh;
    position: relative;
    overflow: hidden;
    font-family: 'DM Sans', system-ui, sans-serif;
  }

  .st-shell::before,
  .st-shell::after {
    content: '';
    position: absolute;
    inset: auto;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(12px);
    opacity: 0.9;
  }

  .st-shell::before {
    width: 360px;
    height: 360px;
    top: -120px;
    left: -80px;
    background: rgba(0, 201, 167, 0.10);
  }

  .st-shell::after {
    width: 420px;
    height: 420px;
    right: -180px;
    bottom: -220px;
    background: rgba(124, 58, 237, 0.10);
  }

  .st-header {
    max-width: 1280px;
    margin: 0 auto;
    padding: 28px 24px 22px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    position: relative;
    z-index: 2;
    margin-top: 5%;
  }

  .st-kicker {
    margin: 0 0 10px;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 11px;
    color: #6e6b80;
    font-weight: 700;
  }

  .st-title {
    margin: 0;
    font-size: clamp(2.1rem, 4.8vw, 4rem);
    line-height: 0.95;
    letter-spacing: -0.06em;
    color: inherit;
  }

  .st-subtitle {
    margin: 12px 0 0;
    max-width: 680px;
    line-height: 1.6;
    color: inherit;
    opacity: 0.76;
  }

  .st-header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .st-header-badge {
    padding: 7px 12px;
    border-radius: 999px;
    border: 1px solid rgba(0, 255, 209, 0.18);
    background: rgba(0, 255, 209, 0.08);
    color: #00ffd1;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }

  .st-ghost-btn {
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.04);
    color: inherit;
    padding: 10px 16px;
    border-radius: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.2s ease, border-color 0.2s ease;
  }

  .st-ghost-btn:hover {
    transform: translateY(-1px);
    border-color: rgba(0, 255, 209, 0.18);
    background: rgba(255, 255, 255, 0.07);
  }

  .st-layout {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px 72px;
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: 20px;
    align-items: start;
    position: relative;
    z-index: 2;
  }

  .st-sidebar,
  .st-panel,
  .st-spotlight {
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(18px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  }

  .st-sidebar {
    overflow: hidden;
    position: sticky;
    top: 24px;
  }

  .st-sidebar-head {
    padding: 20px 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.03);
  }

  .st-sidebar-head p {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: #6e6b80;
  }

  .st-tabs {
    display: flex;
    flex-direction: column;
    padding: 10px;
    gap: 10px;
  }

  .st-tab {
    width: 100%;
    border: 1px solid transparent;
    border-radius: 18px;
    padding: 14px 14px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    text-align: left;
    background: transparent;
    color: inherit;
    transition: transform 0.18s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  }

  .st-tab:hover {
    transform: translateY(-1px);
    border-color: rgba(0, 255, 209, 0.16);
  }

  .st-tab-active {
    background: rgba(0, 255, 209, 0.06);
    border-color: rgba(0, 255, 209, 0.16);
    box-shadow: inset 0 0 0 1px rgba(0, 255, 209, 0.03);
  }

  .st-tab-icon {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    background: rgba(0, 255, 209, 0.08);
    color: #00ffd1;
    border: 1px solid rgba(0, 255, 209, 0.12);
    flex-shrink: 0;
  }

  .st-tab-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .st-tab-label {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  .st-tab-desc {
    font-size: 12px;
    line-height: 1.45;
    opacity: 0.72;
  }

  .st-sidebar-foot {
    padding: 16px 18px 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.02);
  }

  .st-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .st-chip {
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.05);
    color: inherit;
    opacity: 0.82;
  }

  .st-main {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .st-panel {
    padding: 24px;
  }

  .st-panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 22px;
  }

  .st-panel-head h2 {
    margin: 0 0 6px;
    font-size: 22px;
    color: inherit;
    letter-spacing: -0.04em;
  }

  .st-panel-head p {
    margin: 0;
    line-height: 1.55;
    opacity: 0.72;
  }

  .st-status {
    padding: 8px 12px;
    border-radius: 999px;
    background: rgba(0, 255, 209, 0.08);
    border: 1px solid rgba(0, 255, 209, 0.15);
    color: #00ffd1;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }

  .st-theme-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 16px;
  }

  .st-theme-card {
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.08);
    cursor: pointer;
    text-align: left;
    background: rgba(255, 255, 255, 0.02);
    padding: 0;
    color: inherit;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.2s ease;
  }

  .st-theme-card:hover {
    transform: translateY(-2px);
    border-color: rgba(0, 255, 209, 0.16);
    box-shadow: 0 18px 34px rgba(0, 0, 0, 0.16);
  }

  .st-theme-card-selected {
    border-color: rgba(0, 255, 209, 0.4);
    box-shadow: 0 0 0 4px rgba(0, 255, 209, 0.08);
  }

  .st-theme-preview {
    padding: 14px 12px 12px;
  }

  .st-theme-preview-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .st-theme-bars {
    display: flex;
    gap: 4px;
  }

  .st-theme-bars span {
    width: 12px;
    height: 3px;
    border-radius: 999px;
    opacity: 0.6;
  }

  .st-theme-profile {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
  }

  .st-theme-avatar {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    flex-shrink: 0;
    border: 2px solid rgba(255, 255, 255, 0.35);
  }

  .st-theme-lines {
    flex: 1;
    min-width: 0;
  }

  .st-line {
    height: 5px;
    border-radius: 999px;
    margin-bottom: 4px;
  }

  .st-line.short {
    width: 54%;
    height: 3px;
    opacity: 0.65;
  }

  .st-theme-tags {
    display: flex;
    gap: 4px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }

  .st-theme-tags span {
    height: 11px;
    border-radius: 999px;
  }

  .st-theme-hero {
    border-radius: 12px;
    padding: 8px 10px;
  }

  .st-theme-footer {
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: rgba(255, 255, 255, 0.03);
  }

  .st-theme-footer strong {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  .st-check {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 800;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
  }

  .st-preview-shell {
    overflow: hidden;
  }

  .st-preview-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 12px 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.03);
  }

  .st-preview-bar p {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    opacity: 0.75;
  }

  .st-preview-window {
    padding: 20px;
  }

  .st-preview-mock {
    overflow: hidden;
    border-radius: 28px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: #0b1222;
  }

  .st-preview-mock-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(255, 255, 255, 0.04);
    color: rgba(255, 255, 255, 0.72);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .st-preview-hero {
    padding: 22px;
    background:
      radial-gradient(circle at top right, rgba(96, 165, 250, 0.14), transparent 28%),
      radial-gradient(circle at bottom left, rgba(16, 185, 129, 0.08), transparent 24%),
      linear-gradient(180deg, #111c36, #0b1222);
  }

  .st-preview-profilebar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 18px;
  }

  .st-preview-brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .st-preview-avatar {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.18);
  }

  .st-preview-nav {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .st-preview-nav span {
    color: rgba(255, 255, 255, 0.72);
  }

  .st-preview-hero-grid {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 18px;
    align-items: stretch;
    margin-top: 18px;
  }

  .st-preview-hero-copy {
    border-radius: 22px;
    background: rgba(7, 12, 26, 0.88);
    border: 1px solid rgba(255, 255, 255, 0.07);
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 280px;
  }

  .st-preview-name {
    margin: 0;
    font-size: clamp(2.8rem, 5vw, 4.8rem);
    line-height: 0.9;
    letter-spacing: -0.08em;
    font-weight: 900;
    text-transform: uppercase;
    font-style: italic;
    color: #f7f7fb;
  }

  .st-preview-tagline {
    max-width: 32rem;
    margin: 12px 0 0;
    line-height: 1.55;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.64);
  }

  .st-preview-photo {
    border-radius: 22px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01)),
      radial-gradient(circle at top, rgba(0, 255, 209, 0.08), transparent 42%),
      #121a31;
    border: 1px solid rgba(255, 255, 255, 0.07);
    padding: 16px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    min-height: 280px;
    position: relative;
    overflow: hidden;
  }

  .st-preview-photo::before {
    content: '';
    position: absolute;
    inset: 14px;
    border-radius: 18px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02)),
      linear-gradient(135deg, rgba(37, 99, 235, 0.24), rgba(15, 23, 42, 0.06));
  }

  .st-preview-portrait {
    position: relative;
    width: min(100%, 220px);
    aspect-ratio: 0.86;
    border-radius: 20px;
    background:
      radial-gradient(circle at 50% 18%, rgba(255, 255, 255, 0.3), transparent 18%),
      linear-gradient(180deg, #1f2937, #090d17 75%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 18px 30px rgba(0, 0, 0, 0.32);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 18px;
  }

  .st-preview-portrait::before {
    content: '';
    width: 78%;
    height: 84%;
    border-radius: 46% 46% 18% 18% / 42% 42% 10% 10%;
    background:
      radial-gradient(circle at 52% 22%, rgba(255, 226, 196, 0.72), transparent 12%),
      radial-gradient(circle at 50% 28%, rgba(33, 37, 41, 0.95), transparent 18%),
      linear-gradient(180deg, #d8d7d6 0%, #9d9ea0 18%, #2d2b30 18%, #101018 100%);
    filter: contrast(1.02);
    opacity: 0.9;
  }

  .st-preview-badges {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 16px;
    justify-content: space-between;
  }

  .st-preview-badge-pill {
    padding: 8px 12px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.82);
  }

  .st-preview-strip {
    display: flex;
    justify-content: space-around;
    gap: 12px;
    padding: 14px 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(255, 255, 255, 0.03);
    flex-wrap: wrap;
  }

  .st-preview-strip span {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.55);
    text-transform: uppercase;
    letter-spacing: 0.14em;
  }

  .st-preview-strip strong {
    display: block;
    margin-top: 4px;
    font-size: 20px;
    line-height: 1;
    color: #60a5fa;
  }

  .st-preview-sections {
    padding: 18px 20px 22px;
    background: #0b1222;
  }

  .st-preview-section {
    margin-top: 14px;
    padding: 16px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
  }

  .st-preview-section-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .st-preview-section-head h4 {
    margin: 0;
    font-size: 18px;
    letter-spacing: -0.05em;
    text-transform: uppercase;
    font-style: italic;
    color: #f8fafc;
  }

  .st-preview-mini-tag {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    background: rgba(0, 255, 209, 0.10);
    color: #00ffd1;
  }

  .st-preview-grid-2 {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
  }

  .st-preview-grid-3 {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
  }

  .st-preview-card {
    border-radius: 14px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
  }

  .st-preview-card .label {
    margin-bottom: 8px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    opacity: 0.58;
  }

  .st-preview-card .title {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    line-height: 1.4;
    color: #f8fafc;
  }

  .st-preview-card .sub {
    margin: 6px 0 0;
    font-size: 11px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.56);
  }

  .st-preview-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .st-preview-chips span {
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(96, 165, 250, 0.18);
    background: rgba(96, 165, 250, 0.10);
    color: #dbeafe;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .st-notice {
    border-radius: 18px;
    padding: 26px;
    border: 1px dashed rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.03);
    text-align: center;
  }

  .st-notice-icon {
    width: 54px;
    height: 54px;
    border-radius: 18px;
    margin: 0 auto 14px;
    display: grid;
    place-items: center;
    background: rgba(0, 255, 209, 0.08);
    border: 1px solid rgba(0, 255, 209, 0.14);
    color: #00ffd1;
    font-weight: 800;
    font-size: 18px;
  }

  .st-toast {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    padding: 12px 18px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 14px;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
  }

  .st-toast.success {
    border: 1px solid rgba(0, 255, 209, 0.18);
    background: rgba(0, 255, 209, 0.10);
    color: #00ffd1;
  }

  .st-toast.error {
    border: 1px solid rgba(248, 113, 113, 0.18);
    background: rgba(248, 113, 113, 0.10);
    color: #f87171;
  }

  .st-loading {
    display: grid;
    gap: 18px;
  }

  .st-spinner {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 3px solid rgba(255, 255, 255, 0.12);
    border-top-color: #00ffd1;
    animation: stSpin 0.8s linear infinite;
  }

  @keyframes stSpin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 1100px) {
    .st-layout {
      grid-template-columns: 1fr;
    }

    .st-sidebar {
      position: static;
    }
  }

  @media (max-width: 720px) {
    .st-header {
      padding: 24px 16px 18px;
      flex-direction: column;
      align-items: flex-start;
    }

    .st-layout {
      padding: 0 16px 56px;
    }

    .st-panel {
      padding: 18px;
    }

    .st-panel-head {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .st-status {
      max-width: 100%;
      white-space: normal;
      overflow-wrap: anywhere;
      align-self: flex-start;
    }

    .st-preview-window {
      padding: 16px;
    }

    .st-preview-hero {
      grid-template-columns: 1fr;
    }

    .st-stat-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default function Settings({ theme = 'light', toggleTheme }) {
  const [activeTab, setActiveTab] = useState('theme');
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isDark = theme === 'dark';
  const ui = isDark
    ? {
        page: '#050508',
        panel: 'rgba(13, 13, 18, 0.92)',
        card: 'rgba(17, 17, 24, 0.96)',
        border: 'rgba(255, 255, 255, 0.08)',
        text: '#f7f5ff',
        subtext: '#9ca3af',
        muted: '#6e6b80',
        divider: 'rgba(255, 255, 255, 0.08)',
        activeBg: 'rgba(0, 255, 209, 0.08)',
        activeText: '#00ffd1',
        label: '#6e6b80',
        inputBg: '#111118',
        stub: '#111118',
        shadow: '0 16px 40px rgba(0, 0, 0, 0.22)',
      }
    : {
        page: '#f7f8fc',
        panel: 'rgba(255, 255, 255, 0.95)',
        card: '#ffffff',
        border: 'rgba(15, 23, 42, 0.08)',
        text: '#0f172a',
        subtext: '#334155',
        muted: '#64748b',
        divider: 'rgba(15, 23, 42, 0.08)',
        activeBg: 'rgba(37, 99, 235, 0.08)',
        activeText: '#2563eb',
        label: '#94a3b8',
        inputBg: '#f8fafc',
        stub: '#f8fafc',
        shadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
      };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    getProfile()
      .then((data) => setSelectedTheme(data.theme || 'classic'))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleThemeSelect = async (key) => {
    setSelectedTheme(key);
    setSaving(true);
    try {
      await updateTheme(key);
      showToast(`Theme changed to ${THEMES[key].name}`);
    } catch {
      showToast('Failed to save theme', 'error');
    }
    setSaving(false);
  };

  const currentTheme = THEMES[selectedTheme] || THEMES.classic;
  const activeTabMeta = TABS.find((tab) => tab.id === activeTab) || TABS[0];
  const shellBg = buildShellBg(isDark);

  return (
    <div className="st-shell" style={{ background: shellBg, color: ui.text }}>
      <style>{css}</style>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />

      {toast && (
        <div className={`st-toast ${toast.type === 'error' ? 'error' : 'success'}`}>
          {toast.msg}
        </div>
      )}

      <header className="st-header">
        <div>
          <p className="st-kicker">Settings Center</p>
          <h1 className="st-title">Profile settings</h1>
          <p className="st-subtitle">
            Manage your public profile theme and preview a polished user experience before saving.
          </p>
        </div>
      </header>

      <main className="st-layout">
        <aside
          className="st-sidebar"
          style={{
            background: ui.panel,
            borderColor: ui.border,
            boxShadow: ui.shadow,
          }}
        >
          <div className="st-sidebar-head" style={{ borderBottomColor: ui.divider }}>
            <p style={{ color: ui.label }}>Settings</p>
          </div>

          <div className="st-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`st-tab ${activeTab === tab.id ? 'st-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  borderColor: activeTab === tab.id ? ui.activeText : 'transparent',
                  background: activeTab === tab.id ? ui.activeBg : 'transparent',
                }}
              >
                <span
                  className="st-tab-icon"
                  style={{
                    color: activeTab === tab.id ? ui.activeText : ui.text,
                    borderColor: activeTab === tab.id ? `${ui.activeText}33` : ui.border,
                    background: activeTab === tab.id ? `${ui.activeText}14` : ui.stub,
                  }}
                >
                  {tab.icon}
                </span>
                <span className="st-tab-body">
                  <span className="st-tab-label" style={{ color: ui.text }}>
                    {tab.label}
                  </span>
                  <span className="st-tab-desc" style={{ color: ui.subtext }}>
                    {tab.desc}
                  </span>
                </span>
              </button>
            ))}
          </div>

          <div className="st-sidebar-foot" style={{ borderTopColor: ui.divider }}>
            <div className="st-chip-row">
              <span className="st-chip" style={{ color: ui.subtext, background: ui.stub }}>
                {activeTabMeta.label}
              </span>
              <span className="st-chip" style={{ color: ui.subtext, background: ui.stub }}>
                {currentTheme.name}
              </span>
              <span className="st-chip" style={{ color: ui.subtext, background: ui.stub }}>
                Theme Builder
              </span>
            </div>
          </div>
        </aside>

        <section className="st-main">
          {activeTab === 'theme' ? (
            <>
              <section
                className="st-panel"
                style={{
                  background: ui.panel,
                  borderColor: ui.border,
                  boxShadow: ui.shadow,
                }}
              >
                <div className="st-panel-head">
                  <div>
                    <h2>Profile Theme</h2>
                    <p style={{ color: ui.subtext }}>
                      Choose a visual identity for the public profile. The cards below are designed
                      to feel close to the admin experience, but tailored for your portfolio.
                    </p>
                  </div>
                  <span className="st-status">Live preview</span>
                </div>

                {loading ? (
                  <div className="st-loading" style={{ justifyItems: 'center', padding: 40 }}>
                    <div className="st-spinner" />
                    <p style={{ margin: 0, color: ui.subtext, fontWeight: 700 }}>Loading theme data...</p>
                  </div>
                ) : (
                  <div className="st-theme-grid">
                    {Object.entries(THEMES).map(([key, themeMeta]) => {
                      const selected = selectedTheme === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`st-theme-card ${selected ? 'st-theme-card-selected' : ''}`}
                          onClick={() => handleThemeSelect(key)}
                          style={{
                            background: ui.card,
                            borderColor: selected ? themeMeta.primary : ui.border,
                          }}
                        >
                          <div className="st-theme-preview" style={{ background: themeMeta.bg }}>
                            <div className="st-theme-preview-top">
                              <div
                                style={{
                                  width: 36,
                                  height: 5,
                                  borderRadius: 999,
                                  background: themeMeta.primary,
                                }}
                              />
                              <div className="st-theme-bars">
                                {[1, 2, 3].map((i) => (
                                  <span key={i} style={{ background: `${themeMeta.muted}99` }} />
                                ))}
                              </div>
                            </div>

                            <div className="st-theme-profile">
                              <div
                                className="st-theme-avatar"
                                style={{ background: themeMeta.primary, borderColor: themeMeta.accent }}
                              />
                              <div className="st-theme-lines">
                                <div
                                  className="st-line"
                                  style={{ width: '58%', background: themeMeta.text, opacity: 0.92 }}
                                />
                                <div className="st-line short" style={{ background: themeMeta.muted }} />
                              </div>
                            </div>

                            <div className="st-theme-tags">
                              {[38, 30, 46].map((width, index) => (
                                <span
                                  key={index}
                                  style={{
                                    width,
                                    background: `${themeMeta.primary}35`,
                                    border: `1px solid ${themeMeta.primary}40`,
                                  }}
                                />
                              ))}
                            </div>

                            <div className="st-theme-hero" style={{ background: themeMeta.surface }}>
                              <div
                                style={{
                                  width: '36%',
                                  height: 4,
                                  borderRadius: 999,
                                  background: themeMeta.primary,
                                  marginBottom: 8,
                                }}
                              />
                              <div
                                style={{
                                  height: 2,
                                  borderRadius: 999,
                                  background: `${themeMeta.muted}33`,
                                  marginBottom: 4,
                                }}
                              />
                              <div
                                style={{
                                  height: 2,
                                  borderRadius: 999,
                                  background: `${themeMeta.muted}33`,
                                  width: '78%',
                                }}
                              />
                            </div>
                          </div>

                          <div className="st-theme-footer" style={{ background: selected ? themeMeta.primary : ui.stub }}>
                            <strong style={{ color: selected ? '#fff' : ui.text }}>{themeMeta.name}</strong>
                            {selected && <span className="st-check">OK</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              <section
                className="st-panel st-preview-shell"
                style={{
                  background: ui.panel,
                  borderColor: ui.border,
                  boxShadow: ui.shadow,
                }}
              >
                <div className="st-panel-head">
                  <div>
                    <h2>Live Preview</h2>
                    <p style={{ color: ui.subtext }}>
                      A full profile-style mockup that mirrors the professional layout from your
                      reference.
                    </p>
                  </div>
                  {saving && <span className="st-status">Saving...</span>}
                </div>

                <div
                  className="st-preview-window"
                  style={{
                    background: '#0b1222',
                    color: currentTheme.text,
                  }}
                >
                  <div className="st-preview-mock">
                    <div className="st-preview-mock-top">
                      <span>Public Profile</span>
                      <span>Available</span>
                      <span>13 Apr 2026</span>
                    </div>

                    <div className="st-preview-hero">
                      <div className="st-preview-profilebar">
                        <div className="st-preview-brand">
                          <div className="st-preview-avatar" />
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>John Appleseed</div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.58)' }}>UI / UX</div>
                          </div>
                        </div>
                        <div className="st-preview-nav">
                          <span>Work</span>
                          <span>About</span>
                          <span>Experience</span>
                        </div>
                      </div>

                      <div className="st-preview-hero-grid">
                        <div className="st-preview-hero-copy">
                          <div>
                            <div
                              style={{
                                display: 'inline-flex',
                                padding: '6px 12px',
                                borderRadius: 999,
                                background: `${currentTheme.primary}18`,
                                color: currentTheme.primary,
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                marginBottom: 14,
                              }}
                            >
                              Available for work
                            </div>
                            <h3 className="st-preview-name">SDE&apos;S</h3>
                            <p className="st-preview-tagline">
                              Software engineer and product-minded builder creating thoughtful digital
                              experiences with a strong visual system.
                            </p>
                          </div>

                          <div className="st-preview-badges">
                            <span className="st-preview-badge-pill">Frontend</span>
                            <span className="st-preview-badge-pill">React</span>
                            <span className="st-preview-badge-pill">Design Systems</span>
                          </div>
                        </div>

                        <div className="st-preview-photo">
                          <div className="st-preview-portrait" />
                        </div>
                      </div>
                    </div>

                    <div className="st-preview-strip">
                      <div><strong>2</strong><span>Companies</span></div>
                      <div><strong>3</strong><span>Projects</span></div>
                      <div><strong>52</strong><span>Skills</span></div>
                      <div><strong>2</strong><span>Certifications</span></div>
                    </div>

                    <div className="st-preview-sections">
                      <div className="st-preview-section">
                        <div className="st-preview-section-head">
                          <h4>About Me</h4>
                          <span className="st-preview-mini-tag">Personal</span>
                        </div>
                        <div className="st-preview-grid-2">
                          {[
                            ['Current Role', 'Senior Software Engineer'],
                            ['Location', 'Hyderabad'],
                            ['Experience', '6+ years'],
                            ['Focus', 'Product UI'],
                          ].map(([label, value]) => (
                            <div key={label} className="st-preview-card">
                              <div className="label">{label}</div>
                              <p className="title">{value}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="st-preview-section">
                        <div className="st-preview-section-head">
                          <h4>Skills & Technologies</h4>
                          <span className="st-preview-mini-tag">Core stack</span>
                        </div>
                        <div className="st-preview-chips">
                          {['React', 'TypeScript', 'Node', 'Tailwind', 'Figma', 'Redux', 'REST', 'GraphQL', 'Next.js', 'Accessibility'].map((skill) => (
                            <span key={skill}>{skill}</span>
                          ))}
                        </div>
                      </div>

                      <div className="st-preview-section">
                        <div className="st-preview-section-head">
                          <h4>Experience</h4>
                          <span className="st-preview-mini-tag">Timeline</span>
                        </div>
                        <div className="st-preview-grid-2">
                          {[
                            ['Software Developer', 'Built responsive interfaces and scaled design systems.'],
                            ['Software Engineering Intern', 'Worked on UI improvements and product polish.'],
                          ].map(([title, desc]) => (
                            <div key={title} className="st-preview-card">
                              <div className="label">Role</div>
                              <p className="title">{title}</p>
                              <p className="sub">{desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="st-preview-section">
                        <div className="st-preview-section-head">
                          <h4>Projects</h4>
                          <span className="st-preview-mini-tag">Selected work</span>
                        </div>
                        <div className="st-preview-grid-3">
                          {[
                            ['HRMS Platform', 'Internal tools and operations dashboard.'],
                            ['E-commerce App', 'Commerce experience with polished UI states.'],
                            ['AI Support App', 'Assistive interface with clear workflows.'],
                          ].map(([title, desc]) => (
                            <div key={title} className="st-preview-card">
                              <div className="label">Project</div>
                              <p className="title">{title}</p>
                              <p className="sub">{desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <section
              className="st-panel"
              style={{
                background: ui.panel,
                borderColor: ui.border,
                boxShadow: ui.shadow,
              }}
            >
              <div className="st-panel-head">
                <div>
                  <h2>{activeTabMeta.label}</h2>
                  <p style={{ color: ui.subtext }}>These controls are not wired yet, but the new layout is ready for them.</p>
                </div>
                <span className="st-status">Coming soon</span>
              </div>

              <div className="st-notice" style={{ borderColor: ui.border, background: ui.stub }}>
                <div className="st-notice-icon">SOON</div>
                <h3 style={{ margin: '0 0 6px', color: ui.text, letterSpacing: '-0.03em' }}>
                  {activeTabMeta.label} Settings
                </h3>
                <p style={{ margin: 0, color: ui.subtext, lineHeight: 1.6 }}>
                  This section is styled to match the admin experience and can be expanded later with
                  form controls, toggles, and role-based options.
                </p>
              </div>
            </section>
          )}
        </section>
      </main>
    </div>
  );
}
